import QRCode from "qrcode";

export type ModuleShape = "square" | "rounded" | "dots";
export type EyeOuterShape = "square" | "rounded" | "circle";
export type EyeInnerShape = "square" | "circle" | "diamond";

export type StyledOptions = {
  ecc: "L" | "M" | "Q" | "H";
  margin: number; // in modules
  moduleShape: ModuleShape;
  roundness?: number; // 0..0.5 (only for "rounded")
  colors: {
    background: string;
    modules: string;
    eyeOuter: string;
    eyeInner: string;
  };
  eyeOuterShape?: EyeOuterShape;
  eyeInnerShape?: EyeInnerShape;
};

export function buildMatrix(data: string, ecc: StyledOptions["ecc"]): { size: number; modules: boolean[][] } {
  const qr: any = QRCode.create(data, { errorCorrectionLevel: ecc });
  const size: number = qr.modules.size;
  const modules: boolean[][] = [];
  for (let y = 0; y < size; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < size; x++) {
      row.push(Boolean(qr.modules.get(x, y)));
    }
    modules.push(row);
  }
  return { size, modules };
}

export function renderStyledSvg(
  matrix: { size: number; modules: boolean[][] },
  opts: StyledOptions
): string {
  const size = matrix.size | 0;
  const margin = Math.max(0, Math.floor(opts.margin | 0));
  const total = size + margin * 2;
  const moduleColor = opts.colors.modules;
  const bgColor = opts.colors.background;
  const eyeOuterColor = opts.colors.eyeOuter;
  const eyeInnerColor = opts.colors.eyeInner;
  const moduleShape = opts.moduleShape;
  const roundness = clamp(opts.roundness ?? 0.35, 0, 0.5);
  const eyeOuterShape: EyeOuterShape = opts.eyeOuterShape ?? "square";
  const eyeInnerShape: EyeInnerShape = opts.eyeInnerShape ?? "square";

  const parts: string[] = [];

  // SVG root + background
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${total} ${total}" width="${total}" height="${total}">`
  );
  parts.push(`<rect x="0" y="0" width="${total}" height="${total}" fill="${escapeAttr(bgColor)}"/>`);

  // Finder eye regions (module coordinates)
  const eyes = [
    { x: 0, y: 0 },
    { x: size - 7, y: 0 },
    { x: 0, y: size - 7 },
  ];

  const inFinder = (x: number, y: number): boolean => {
    for (const e of eyes) {
      if (x >= e.x && x < e.x + 7 && y >= e.y && y < e.y + 7) return true;
    }
    return false;
  };

  // Draw data modules (skip finder regions)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (!matrix.modules[y][x]) continue;
      if (inFinder(x, y)) continue;
      const px = margin + x;
      const py = margin + y;
      if (moduleShape === "square") {
        parts.push(
          `<rect data-part="module" x="${px}" y="${py}" width="1" height="1" fill="${escapeAttr(moduleColor)}"/>`
        );
      } else if (moduleShape === "rounded") {
        const rx = roundness;
        parts.push(
          `<rect data-part="module" x="${px}" y="${py}" width="1" height="1" rx="${rx}" ry="${rx}" fill="${escapeAttr(
            moduleColor
          )}"/>`
        );
      } else if (moduleShape === "dots") {
        const cx = px + 0.5;
        const cy = py + 0.5;
        parts.push(`<circle data-part="module" cx="${cx}" cy="${cy}" r="0.45" fill="${escapeAttr(moduleColor)}"/>`);
      }
    }
  }

  // Draw eyes (outer ring + inner center), leaving the 1-module light separator between 5x5 and 3x3
  for (const e of eyes) {
    const ox = margin + e.x;
    const oy = margin + e.y;
    // Outer ring covers 7x7 with inner hole 5x5 (we simulate by drawing outer, then overlaying inner hole with background)
    if (eyeOuterShape === "square") {
      // Even-odd path ring for crisp edges
      const d = [
        // Outer 7x7
        `M ${ox} ${oy} h 7 v 7 h -7 Z`,
        // Inner 5x5 hole
        `M ${ox + 1} ${oy + 1} h 5 v 5 h -5 Z`,
      ].join(" ");
      parts.push(
        `<path data-eye="outer" d="${d}" fill-rule="evenodd" fill="${escapeAttr(eyeOuterColor)}"/>`
      );
    } else if (eyeOuterShape === "rounded") {
      const rx = Math.min(2, Math.max(0, roundness * 2));
      parts.push(
        `<rect data-eye="outer" x="${ox}" y="${oy}" width="7" height="7" rx="${rx}" ry="${rx}" fill="${escapeAttr(
          eyeOuterColor
        )}"/>`
      );
      parts.push(
        `<rect x="${ox + 1}" y="${oy + 1}" width="5" height="5" rx="${rx}" ry="${rx}" fill="${escapeAttr(bgColor)}"/>`
      );
    } else if (eyeOuterShape === "circle") {
      const cx = ox + 3.5;
      const cy = oy + 3.5;
      parts.push(`<circle data-eye="outer" cx="${cx}" cy="${cy}" r="3.5" fill="${escapeAttr(eyeOuterColor)}"/>`);
      parts.push(`<circle cx="${cx}" cy="${cy}" r="2.5" fill="${escapeAttr(bgColor)}"/>`);
    }

    // Inner center (3x3)
    if (eyeInnerShape === "square") {
      parts.push(
        `<rect data-eye="inner" x="${ox + 2}" y="${oy + 2}" width="3" height="3" fill="${escapeAttr(eyeInnerColor)}"/>`
      );
    } else if (eyeInnerShape === "circle") {
      const cx = ox + 3.5;
      const cy = oy + 3.5;
      parts.push(`<circle data-eye="inner" cx="${cx}" cy="${cy}" r="1.5" fill="${escapeAttr(eyeInnerColor)}"/>`);
    } else if (eyeInnerShape === "diamond") {
      const cx = ox + 3.5;
      const cy = oy + 3.5;
      const r = 1.5;
      const d = `M ${cx} ${cy - r} L ${cx + r} ${cy} L ${cx} ${cy + r} L ${cx - r} ${cy} Z`;
      parts.push(`<path data-eye="inner" d="${d}" fill="${escapeAttr(eyeInnerColor)}"/>`);
    }
  }

  parts.push("</svg>");
  return parts.join("");
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function escapeAttr(value: string): string {
  return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}


